/**
 * Secure links (ACTION_LINKS_PLAN.md): what staff asked this client to do, pinned under the top bar until
 * it is done, expires or is withdrawn. Loaded at sign-in, then kept live from the user's WS channel.
 */
import { useEffect } from 'react';
import type { ClientActionLink } from '@platform/shared';
import { api } from '../lib/api';
import { createStore, useStore } from '../lib/store';
import { useMe } from './session';

interface ActionLinkState {
  links: ClientActionLink[];
  /** A link the client just finished: thanked for a few seconds. */
  thanked: boolean;
}

export const actionLinkStore = createStore<ActionLinkState>({ links: [], thanked: false });

const THANKS_MS = 6000;

export async function loadActionLinks() {
  try {
    const r = await api.get<{ data: ClientActionLink[] }>('/api/action-links');
    actionLinkStore.set({ links: r.data });
  } catch {
    /* the cards just stay as they were */
  }
}

/** WS `action_link`: a new link (a replaced one closes separately). */
export function actionLinkArrived(link: ClientActionLink) {
  actionLinkStore.set((s) => ({ links: [link, ...s.links.filter((l) => l.id !== link.id)] }));
}

/** WS `action_link_closed`. */
export function actionLinkClosed(id: string, status: string) {
  const had = actionLinkStore.get().links.some((l) => l.id === id);
  actionLinkStore.set((s) => ({ links: s.links.filter((l) => l.id !== id) }));
  if (had && status === 'completed') {
    actionLinkStore.set({ thanked: true });
    window.setTimeout(() => actionLinkStore.set({ thanked: false }), THANKS_MS);
  }
}

/**
 * Where the link goes, or null when it is no longer active (whatever the reason). `external`: a custom
 * link's https address, which closed on this open.
 */
export async function openActionLink(token: string): Promise<{ to: string; external: boolean } | null> {
  const r = await api.post<{ active: boolean; opens?: string; external?: boolean }>('/api/action-links/open', { token });
  return r.active && r.opens ? { to: r.opens, external: !!r.external } : null;
}

/** Mount once: (re)load when the signed-in user changes. */
export function useActionLinksSync() {
  const userId = useMe()?.id ?? null;
  useEffect(() => {
    actionLinkStore.set({ links: [], thanked: false });
    if (userId) void loadActionLinks();
  }, [userId]);
}

export const useActionLinks = () => useStore(actionLinkStore, (s) => s.links);
export const useActionLinkThanks = () => useStore(actionLinkStore, (s) => s.thanked);
